/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

/**
 * Browser-side identity stripper (S5b).
 *
 * User drops their raw LinkedIn Connections.csv. This component:
 *   1. Reads the file into memory (up to 10MB).
 *   2. Strips LinkedIn's 3-line preamble.
 *   3. Keeps only Company and Position columns — every other column
 *      (First Name, Last Name, URL, Email, Connected On) is dropped
 *      before anything is uploaded.
 *   4. Adds a row_id (arbitrary sequence number).
 *   5. Shows a preview + two buttons: [Upload] and [Download stripped
 *      file]. Nothing uploads during conversion — download-and-inspect
 *      is always an option.
 *
 * No zip dependency in v1 — copy asks users to unzip first and drop
 * the individual Connections.csv.
 *
 * Zero new npm dependencies.
 */

import React, { useCallback, useState } from 'react';

const MAX_BYTES = 10 * 1024 * 1024; // 10 MB

// LinkedIn's native export columns. If we see FEWER than 2 of these
// in the header, it's probably not a LinkedIn export.
const LINKEDIN_SIGNATURE_COLS = [
  'first name', 'last name', 'url', 'email address',
  'company', 'position', 'connected on'
];

/**
 * Parse a single CSV line honouring double-quoted fields with commas.
 */
function parseLine(line) {
  const values = [];
  let cur = '', inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') inQuotes = !inQuotes;
    else if (ch === ',' && !inQuotes) { values.push(cur); cur = ''; }
    else cur += ch;
  }
  values.push(cur);
  return values.map(v => v.trim());
}

/**
 * Wrap a CSV field: quote if it contains a comma, quote, or newline.
 */
function csvField(v) {
  const s = String(v || '');
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/**
 * Strip identity, return a platform-format CSV blob + counts.
 */
function convert(text) {
  const clean = text.charCodeAt(0) === 0xFEFF ? text.slice(1) : text;
  const lines = clean.replace(/\r\n/g, '\n').split('\n');

  // Skip lines until we find one that looks like a header row (starts
  // with the LinkedIn signature: "First Name," or contains 2+ signature cols).
  let headerIdx = -1;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    const lower = line.toLowerCase();
    const hits = LINKEDIN_SIGNATURE_COLS.filter(c => lower.includes(c)).length;
    if (hits >= 2) { headerIdx = i; break; }
  }
  if (headerIdx === -1) {
    throw new Error("This doesn't look like a LinkedIn Connections.csv export. Make sure you unzipped the LinkedIn download and picked Connections.csv.");
  }

  const headers = parseLine(lines[headerIdx]).map(h => h.toLowerCase());
  const companyIdx = headers.indexOf('company');
  const positionIdx = headers.indexOf('position');
  if (companyIdx === -1 || positionIdx === -1) {
    throw new Error("Couldn't find Company and Position columns. Is this a LinkedIn Connections export?");
  }

  const outLines = ['row_id,company,position'];
  let rowsKept = 0;
  let rowsSkipped = 0;

  for (let i = headerIdx + 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) { rowsSkipped++; continue; }
    const values = parseLine(line);
    const company = (values[companyIdx] || '').trim();
    const position = (values[positionIdx] || '').trim();
    // Keep even rows with only company or only position — they still
    // yield a Connection node. Skip if BOTH are blank.
    if (!company && !position) { rowsSkipped++; continue; }
    rowsKept++;
    outLines.push(`${rowsKept},${csvField(company)},${csvField(position)}`);
  }

  if (rowsKept === 0) {
    throw new Error("No usable rows found in the file.");
  }

  const csv = outLines.join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const sample = outLines.slice(1, 6).map(l => l.split(',').slice(1).map(v => v.replace(/^"|"$/g, '')).join(' | '));

  return { blob, csv, rowsKept, rowsSkipped, sample };
}

export default function LinkedInConverter({ onUploadReady, disabled }) {
  const [state, setState] = useState({
    phase: 'idle',   // idle | converted | error
    result: null,
    error: null,
    fileName: null
  });

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    setState({ phase: 'idle', result: null, error: null, fileName: file.name });

    if (file.size > MAX_BYTES) {
      setState({ phase: 'error', error: `File is too large (${(file.size/1024/1024).toFixed(1)}MB, max 10MB).`, result: null, fileName: file.name });
      return;
    }
    try {
      const text = await file.text();
      const result = convert(text);
      setState({ phase: 'converted', result, error: null, fileName: file.name });
    } catch (e) {
      setState({ phase: 'error', error: e.message, result: null, fileName: file.name });
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    if (disabled) return;
    handleFile(e.dataTransfer.files?.[0]);
  }, [handleFile, disabled]);

  const handleDownload = () => {
    if (!state.result) return;
    const url = URL.createObjectURL(state.result.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'network.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleUpload = () => {
    if (!state.result) return;
    const file = new File([state.result.blob], 'network.csv', { type: 'text/csv' });
    onUploadReady(file, {
      rowsKept: state.result.rowsKept,
      rowsSkipped: state.result.rowsSkipped
    });
  };

  return (
    <div>
      <div style={{ marginBottom: '16px', fontSize: '13px', color: '#475569' }}>
        <strong>Have a LinkedIn export?</strong> Drop your <code>Connections.csv</code> here.
        Company and role are extracted; names, emails, URLs and dates are
        stripped before anything uploads.
        <br />
        <em style={{ color: '#64748b' }}>
          If your download is a .zip, unzip it first and pick the file named Connections.csv.
        </em>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={handleDrop}
        style={dropzoneStyle(disabled)}
      >
        {state.fileName ? (
          <div>
            <div style={{ fontWeight: 600 }}>{state.fileName}</div>
            {state.phase === 'converted' && state.result && (
              <div style={{ marginTop: '8px', fontSize: '13px', color: '#0f766e' }}>
                Converted {state.result.rowsKept.toLocaleString()} rows
                {state.result.rowsSkipped > 0 && `, skipped ${state.result.rowsSkipped}`}.
              </div>
            )}
            {state.phase === 'error' && (
              <div style={{ marginTop: '8px', fontSize: '13px', color: '#b91c1c' }}>
                {state.error}
              </div>
            )}
          </div>
        ) : (
          <div>
            <div style={{ fontSize: '32px' }}>📄</div>
            <div>Drop Connections.csv here, or click to browse</div>
          </div>
        )}
        <input
          type="file"
          accept=".csv"
          onChange={(e) => handleFile(e.target.files?.[0])}
          disabled={disabled}
          style={fileInputStyle}
        />
      </div>

      {state.phase === 'converted' && state.result && (
        <>
          <div style={previewStyle}>
            <div style={{ fontWeight: 600, marginBottom: '8px' }}>Preview (first 5 rows):</div>
            {state.result.sample.map((row, i) => (
              <div key={i} style={{ fontFamily: 'monospace', fontSize: '12px' }}>{row}</div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
            <button
              type="button"
              onClick={handleUpload}
              disabled={disabled}
              style={primaryBtnStyle}
            >
              Upload converted file
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={disabled}
              style={secondaryBtnStyle}
            >
              Download stripped file
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const dropzoneStyle = (disabled) => ({
  border: '2px dashed #cbd5e1',
  borderRadius: '8px',
  padding: '32px 20px',
  textAlign: 'center',
  background: disabled ? '#f1f5f9' : '#f8fafc',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.5 : 1,
  position: 'relative'
});

const fileInputStyle = {
  position: 'absolute',
  inset: 0,
  opacity: 0,
  cursor: 'pointer'
};

const previewStyle = {
  marginTop: '16px',
  padding: '12px',
  background: '#f8fafc',
  borderRadius: '6px',
  fontSize: '13px'
};

const primaryBtnStyle = {
  padding: '10px 20px',
  background: '#00BCD4',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer'
};

const secondaryBtnStyle = {
  padding: '10px 20px',
  background: '#fff',
  border: '1px solid #cbd5e1',
  borderRadius: '6px',
  fontSize: '14px',
  cursor: 'pointer'
};
